import cx from 'classnames';
import { type FC, type ReactNode, useEffect } from 'react';

import { staticBlockRequest } from 'api/cms';
import Loader from 'components/Loader';
import { useLanguage } from 'store';
import { LoadingState } from 'utils/types.ts';
import useRequest from 'utils/useRequest.ts';

import styles from './StaticBlock.module.scss';

type StaticBlockProps = {
  className?: string;
} & (
  | {
      name: string;
      fallback?: ReactNode;
    }
  | {
      name?: string;
      fallback: ReactNode;
    }
);

const StaticBlock: FC<StaticBlockProps> = ({
  name = '',
  fallback,
  className,
}) => {
  const [language] = useLanguage();

  const { data, loading, error } = useRequest(
    () => staticBlockRequest({ query: { name, lang: language.current.code } }),
    [name, language.current.code],
  );

  useEffect(() => {
    if (error) {
      console.warn(`Failed to load static block "${name}"`, error);
    }
  }, [error, name]);

  if (loading === LoadingState.loading) {
    return <Loader />;
  }

  if (loading === LoadingState.success && data?.data) {
    return (
      <div
        className={cx(className, styles.wrapper)}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: data from CMS
        dangerouslySetInnerHTML={{ __html: data.data }}
      />
    );
  }

  if ((loading === LoadingState.success && !data?.data) || error) {
    return fallback;
  }

  return null;
};

export default StaticBlock;
